import { z } from 'zod';
import { FIELD_TYPES, type FieldType } from '../enums.js';

/** Media reference embedded in an image/og_image value. */
export const ImageValueSchema = z.object({
  assetId: z.uuid().nullish(),
  src: z.string().min(1).max(2048),
  alt: z.string().max(500).default(''),
  width: z.number().int().positive().nullish(),
  height: z.number().int().positive().nullish(),
  srcset: z.string().max(2048).nullish(),
});
export type ImageValue = z.infer<typeof ImageValueSchema>;

export const FileValueSchema = z.object({
  assetId: z.uuid().nullish(),
  src: z.string().min(1).max(2048),
  filename: z.string().max(255).nullish(),
  sizeBytes: z.number().int().nonnegative().nullish(),
  mimeType: z.string().max(255).nullish(),
});
export type FileValue = z.infer<typeof FileValueSchema>;

export const LinkValueSchema = z.object({
  href: z.string().min(1).max(2048),
  text: z.string().max(500).nullish(),
  target: z.enum(['_self', '_blank', '_parent', '_top']).nullish(),
  rel: z.string().max(200).nullish(),
});
export type LinkValue = z.infer<typeof LinkValueSchema>;

/** Field configuration stored on `content_model_fields.config`. */
export interface FieldConfig {
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  step?: number;
  accept?: string[];
  /** Repeater/group child fields (recursive). */
  fields?: FieldDefinition[];
  minItems?: number;
  maxItems?: number;
  placeholder?: string;
  defaultValue?: unknown;
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  helpText?: string | null;
  config?: FieldConfig;
}

export const FieldConfigSchema: z.ZodType<FieldConfig> = z.lazy(() =>
  z.object({
    options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    maxLength: z.number().int().positive().optional(),
    step: z.number().optional(),
    accept: z.array(z.string()).optional(),
    fields: z.array(FieldDefinitionSchema).optional(),
    minItems: z.number().int().nonnegative().optional(),
    maxItems: z.number().int().positive().optional(),
    placeholder: z.string().optional(),
    defaultValue: z.unknown().optional(),
  }),
) as z.ZodType<FieldConfig>;

export const FieldDefinitionSchema: z.ZodType<FieldDefinition> = z.lazy(() =>
  z.object({
    key: z.string().min(1).max(120),
    label: z.string().min(1).max(200),
    type: z.enum(FIELD_TYPES),
    required: z.boolean().optional(),
    helpText: z.string().max(500).nullish(),
    config: FieldConfigSchema.optional(),
  }),
) as z.ZodType<FieldDefinition>;

const textish = (max = 5000) => z.string().max(max);

/**
 * Returns the zod schema that validates the stored `value` of an entry/field.
 * Used by the API on save, by the dashboard form resolver and by the CLI.
 */
export function valueSchemaFor(type: FieldType, config?: FieldConfig): z.ZodType {
  switch (type) {
    case 'text':
    case 'seo_title':
      return textish(config?.maxLength ?? 500);
    case 'longtext':
    case 'seo_description':
      return textish(config?.maxLength ?? 5000);
    case 'richtext':
      return textish(config?.maxLength ?? 200_000);
    case 'number': {
      let schema = z.number();
      if (config?.min !== undefined) schema = schema.min(config.min);
      if (config?.max !== undefined) schema = schema.max(config.max);
      return schema;
    }
    case 'boolean':
      return z.boolean();
    case 'datetime':
      return z.iso.datetime({ offset: true });
    case 'image':
    case 'og_image':
      return ImageValueSchema;
    case 'file':
      return FileValueSchema;
    case 'link':
      return LinkValueSchema;
    case 'email':
      return z.email().max(320);
    case 'phone':
      return z.string().max(40).regex(/^[+0-9 ()./-]{3,40}$/, 'Invalid phone number');
    case 'color':
      return z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'Expected a hex colour');
    case 'canonical_url':
      return z.string().max(2048);
    case 'select': {
      const options = config?.options?.map((o) => o.value);
      return options?.length ? z.enum(options as [string, ...string[]]) : z.string().max(200);
    }
    case 'multiselect': {
      const options = config?.options?.map((o) => o.value);
      const item = options?.length ? z.enum(options as [string, ...string[]]) : z.string().max(200);
      return z.array(item).max(100);
    }
    case 'json':
      return z.unknown();
    case 'group':
      return objectSchemaFor(config?.fields ?? []);
    case 'repeater': {
      let arr = z.array(objectSchemaFor(config?.fields ?? []));
      if (config?.minItems !== undefined) arr = arr.min(config.minItems);
      arr = arr.max(config?.maxItems ?? 500);
      return arr;
    }
    default:
      return z.unknown() as z.ZodType;
  }
}

/** Builds an object schema from child field definitions (group/repeater items). */
export function objectSchemaFor(fields: readonly FieldDefinition[]): z.ZodType {
  if (!fields.length) return z.record(z.string(), z.unknown());
  const shape: Record<string, z.ZodType> = {};
  for (const field of fields) {
    const schema = valueSchemaFor(field.type, field.config);
    shape[field.key] = field.required ? schema : schema.nullish();
  }
  return z.object(shape);
}

/** Sensible empty value used when creating a registry entry with no content yet. */
export function emptyValueFor(type: FieldType): unknown {
  switch (type) {
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'repeater':
    case 'multiselect':
      return [];
    case 'group':
    case 'json':
      return {};
    case 'image':
    case 'og_image':
      return { src: '', alt: '' };
    case 'file':
      return { src: '' };
    case 'link':
      return { href: '#', text: '' };
    default:
      return '';
  }
}
